import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Check, ArrowUpRight } from "lucide-react";
import { getService, SERVICES } from "@/data/services";
import { Reveal, waLink } from "@/components/landing/shared";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <main data-testid="service-detail-page" className="bg-void">
      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={service.hero} alt={service.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/90 to-void" />
        </div>
        <div className="absolute -top-20 left-1/4 w-[26rem] h-[26rem] rounded-full bg-brand-lime/10 blur-[150px] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Link
            data-testid="service-back-link"
            to="/"
            state={{ scrollTo: "#services" }}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-brand-lime transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mt-8 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full border-2 border-brand-lime/60 bg-brand-lime/10 flex items-center justify-center text-brand-lime shadow-[0_0_22px_rgba(170,255,0,0.3)]">
                <service.icon className="w-6 h-6" />
              </div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-lime">
                Chapter {service.num} · {service.eta}
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              {service.title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">{service.tagline}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                data-testid="service-detail-whatsapp-button"
                href={waLink(`Hi AFZ Tech Solution, I need help with: ${service.title}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-brand-lime text-void font-display font-bold uppercase text-base px-7 py-4 rounded-full hover:bg-brand-limedark hover:scale-[1.02] transition-all duration-300 shadow-[0_0_30px_rgba(170,255,0,0.4)]"
              >
                <MessageCircle className="w-5 h-5" /> Book on WhatsApp
              </a>
              <Link
                to="/"
                state={{ scrollTo: "#calculator" }}
                className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 font-display font-bold uppercase text-base px-7 py-4 rounded-full hover:border-brand-lime hover:text-brand-lime transition-all duration-300"
              >
                Get a Price Estimate
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <h2 className="font-display text-2xl font-bold uppercase text-white">Overview</h2>
              <p className="mt-4 text-slate-400 leading-relaxed">{service.intro}</p>
              <h3 className="mt-8 font-display text-lg font-bold uppercase text-white">What we handle</h3>
              <ul className="mt-4 space-y-3">
                {service.handles.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-lime shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-300">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-card/80 border border-slate-800 rounded-2xl p-8 clip-corner">
              <h3 className="font-display text-lg font-bold uppercase text-white">How it works</h3>
              <ol className="mt-6 space-y-6">
                {service.steps.map((s, i) => (
                  <li key={s.t} className="flex items-start gap-4">
                    <span className="shrink-0 w-9 h-9 rounded-xl border border-slate-700 bg-void flex items-center justify-center font-display font-bold text-brand-lime">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{s.t}</p>
                      <p className="text-slate-400 text-sm mt-1 leading-relaxed">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-slate-800">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-cyan">
                  90-day workmanship warranty · Kembangan workshop
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Other services */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-2xl font-bold uppercase text-white">Other services</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {others.map((s) => (
              <Link
                key={s.slug}
                data-testid={`related-service-${s.slug}`}
                to={`/services/${s.slug}`}
                className="group bg-card/80 border border-slate-800 rounded-2xl p-6 hover:border-brand-lime/50 transition-all duration-300 hover:-translate-y-1"
              >
                <s.icon className="w-6 h-6 text-brand-lime" />
                <h3 className="mt-4 font-display text-lg font-bold uppercase text-white leading-tight">{s.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 group-hover:text-brand-lime transition-colors">
                  View <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
